import {useEffect, useState} from 'react';
import {AlertTriangle, CheckCircle2, Loader2, RotateCcw} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {Button} from '@/components/ui/Button';
import {Input} from '@/components/ui/Input';
import {Badge} from '@/components/ui/Badge';
import {
  defaultResendRange,
  describeResendError,
  rangeDayCount,
  toUtcDayBound,
  transmissionCadence,
  useResendTransmission,
} from '@/lib/api/transmissions';
import {formatDate} from '@/lib/utils/format';
import type {TransmissionKind} from '@/lib/api/types';

interface Props {
  /** Which stream is being replayed — decides whether there's a range at all. */
  kind: TransmissionKind;
  /** Wire name of the integration to replay, e.g. `siemens-pos`. */
  integration: string;
  /** Provider label, for prose the user recognises. */
  providerLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Range to prefill. Set when the dialog is opened from a POS row, so
   * replaying one failed run is a click and a confirm rather than retyping its
   * window out of the table. Ignored on the inventory stream, which has none.
   */
  initialRange?: {from?: string; to?: string};
}

/** Today as the viewer's calendar shows it — used only for the "in the future" check below. */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Advisory only — the backend imposes no width limit. A month is where a replay
// stops being "fix yesterday" and starts being a bulk re-transmit worth a
// second look, so the count is stated plainly rather than blocked.
const WIDE_RANGE_DAYS = 31;

export function ResendTransmissionDialog({
  kind,
  integration,
  providerLabel,
  open,
  onOpenChange,
  initialRange,
}: Props) {
  const isPos = kind === 'pos';
  const resend = useResendTransmission();
  const cadence = transmissionCadence(integration);
  // Not from a row: default to the window the schedule itself would currently
  // send, so replaying "now" reproduces what actually goes out.
  const [from, setFrom] = useState(
    () => initialRange?.from ?? defaultResendRange(integration).from
  );
  const [to, setTo] = useState(
    () => initialRange?.to ?? defaultResendRange(integration).to
  );

  // Re-seed on each open. The state lives outside the portal, so it survives a
  // close — without this, opening from a second row would show the first row's
  // window, or a stale success screen.
  useEffect(() => {
    if (!open) return;
    const fallback = defaultResendRange(integration);
    setFrom(initialRange?.from ?? fallback.from);
    setTo(initialRange?.to ?? fallback.to);
    resend.reset();
    // `resend` is a fresh object every render; depending on it would re-seed
    // the fields mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integration, initialRange?.from, initialRange?.to]);

  const startBound = toUtcDayBound(from, 'start');
  const endBound = toUtcDayBound(to, 'end');
  const days = rangeDayCount(from, to);
  const inverted = Boolean(startBound && endBound && startBound > endBound);
  // An inventory replay takes no arguments at all, so there is nothing it
  // could get wrong — the button is live the moment the dialog opens.
  const canSubmit = isPos ? Boolean(startBound && endBound && !inverted) : true;
  const inFuture = Boolean(startBound && from > today());

  const accepted = resend.data?.data;
  const failure = resend.isError
    ? describeResendError(resend.error, kind)
    : null;

  // A replay in flight is already sending documents downstream; letting the
  // dialog close would hide the outcome of something that cannot be undone.
  const handleOpenChange = (next: boolean) => {
    if (!next && resend.isPending) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (!canSubmit) return;
    resend.mutate(
      isPos
        ? {kind: 'pos', integration, startDate: from, endDate: to}
        : {kind: 'inventory', integration}
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby="resend-description"
        onInteractOutside={e => {
          if (resend.isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            Resend {providerLabel} {isPos ? 'POS transmission' : 'stock table'}
          </DialogTitle>
          <DialogDescription id="resend-description">
            {cadence?.summary} Submitting sends to {providerLabel} right now —
            not a preview.
          </DialogDescription>
        </DialogHeader>

        {accepted ? (
          <div className="space-y-3 px-6 pb-6">
            <p className="flex items-start gap-2 text-sm text-success">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {resend.data?.message ??
                  'Replay accepted. The run reports back on its own schedule — new rows appear in the log once it does.'}
              </span>
            </p>
            <dl className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
              <div className="flex justify-between gap-4 py-0.5">
                <dt className="text-muted-foreground">Integration</dt>
                <dd className="font-mono">{accepted.integration}</dd>
              </div>
              {/* An inventory replay echoes back no window, because it was
                  sent none — don't render two empty rows for it. */}
              {accepted.startDate && (
                <div className="flex justify-between gap-4 py-0.5">
                  <dt className="text-muted-foreground">From</dt>
                  <dd className="font-mono">{accepted.startDate}</dd>
                </div>
              )}
              {accepted.endDate && (
                <div className="flex justify-between gap-4 py-0.5">
                  <dt className="text-muted-foreground">To</dt>
                  <dd className="font-mono">{accepted.endDate}</dd>
                </div>
              )}
            </dl>
          </div>
        ) : (
          <div className="space-y-4 px-6 pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Replaying
              <Badge variant="muted" className="font-mono">
                {integration}
              </Badge>
            </div>

            {isPos ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-muted-foreground">
                      First document day
                    </span>
                    <Input
                      type="date"
                      value={from}
                      max={to || undefined}
                      disabled={resend.isPending}
                      onChange={e => setFrom(e.target.value)}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-muted-foreground">
                      Last document day
                    </span>
                    <Input
                      type="date"
                      value={to}
                      min={from || undefined}
                      disabled={resend.isPending}
                      onChange={e => setTo(e.target.value)}
                    />
                  </label>
                </div>

                {/* The exact instants, spelled out. The bounds are UTC while
                    the pickers show local days, so the two can name different
                    dates near midnight — support should not have to infer
                    which. */}
                {canSubmit && (
                  <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
                    <p className="font-medium">
                      {days === 1
                        ? formatDate(from)
                        : `${formatDate(from)} – ${formatDate(to)}`}
                      <span className="ml-2 font-normal text-muted-foreground">
                        {days} day{days === 1 ? '' : 's'}
                      </span>
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {startBound} → {endBound}
                    </p>
                  </div>
                )}

                {inverted && (
                  <p className="text-xs text-warning">
                    The first day is after the last day, so the range selects
                    nothing.
                  </p>
                )}

                {!inverted && (!startBound || !endBound) && (
                  <p className="text-xs text-warning">
                    Pick both a first and a last document day.
                  </p>
                )}

                {canSubmit && inFuture && (
                  <p className="flex items-start gap-2 text-xs text-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    That window starts in the future — no documents are dated in
                    it yet.
                  </p>
                )}

                {canSubmit && days != null && days > WIDE_RANGE_DAYS && (
                  <p className="flex items-start gap-2 text-xs text-warning">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {days} days — a bulk re-transmit. Documents already landed
                    get sent again too.
                  </p>
                )}
              </>
            ) : (
              // Ingestion overwrites the stock table in place, so there's no
              // earlier version left to replay — a resend always sends *now*.
              <p className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span>
                  No window to pick — this sends the stock table as it stands
                  right now, not the figures from the run you're repairing.
                </span>
              </p>
            )}

            {failure && (
              <p role="alert" className="text-xs text-destructive">
                {failure.status ? `${failure.status} — ` : ''}
                {failure.message}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          {accepted ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={resend.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit || resend.isPending}
              >
                {resend.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                {resend.isPending
                  ? 'Resending…'
                  : !isPos
                    ? 'Resend stock table'
                    : days === 1
                      ? 'Resend 1 day'
                      : `Resend ${days ?? ''} days`.trim()}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
